import { ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

import { isValid } from 'date-fns'
import enUS from 'date-fns/locale/en-US'

import { useDebounce } from '../../hooks'
import { DateFormatType, getCustomDateFormat } from '../../utils'
import { DatePicker, DatePickerProps } from '../DatePicker/DatePicker'
import { Copy } from '../DatePicker/DatePickerCopy'
import Input, { InputProps } from '../Input/Input'
import { DatePickerInputClickOutside } from './DatePickerInputClickOutside'

export type GetDateFormatType = () => DateFormatType
export interface DatePickerInputProps extends InputProps {
  /** DatePickerInput cannot be wrapped in FieldLabel, so we need to pass in an associated id to pair the input with the label */
  id: string

  copy?: Copy
  picker?: Omit<DatePickerProps, 'children' | 'copy' | 'open' | 'onClose' | 'selected' | 'month'>
  getDateFormat?: GetDateFormatType
}

export function getFormattedDateFromDate(date: Date, getDateFormat: GetDateFormatType) {
  const { format } = getDateFormat()
  return format
    .replace('MM', String(date.getMonth() + 1).padStart(2, '0'))
    .replace('DD', String(date.getDate()).padStart(2, '0'))
    .replace('YYYY', String(date.getFullYear()))
}

export function getDateFromParsedDate(parsed?: { day?: string; month?: string; year?: string }) {
  if (!parsed?.year || !parsed.month || !parsed.day) return undefined
  const date = new Date(parseInt(parsed.year), parseInt(parsed.month) - 1, parseInt(parsed.day))
  return isValid(date) ? date : undefined
}

export function getDateFromFormattedDate(formattedDate: string, getDateFormat: GetDateFormatType) {
  const { parseDate } = getDateFormat()
  const parsed = parseDate(formattedDate)
  return getDateFromParsedDate(parsed)
}

export function isValidFullYear(year?: number | string) {
  return parseInt(String(year ?? '')).toString().length === 4
}

function useVisibleMonth(value: string, getDateFormat: GetDateFormatType, defaultMonth?: Date) {
  const computeMonth = useCallback(() => {
    const dateFormat = getDateFormat()
    const parsedDate = dateFormat?.parseDate(value)

    const fallback = defaultMonth ?? new Date()
    const fallbackYear = fallback.getFullYear()
    const fallbackMonth = fallback.getMonth() + 1

    const getValidYear = () => {
      if (!parsedDate || !parsedDate.year) return fallbackYear
      const year = parseInt(parsedDate.year)
      return isValidFullYear(year) ? year : fallbackYear
    }

    const getValidMonth = () => {
      const month = parsedDate && parseInt(parsedDate.month)
      return month && month > 0 && month <= 12 ? month : fallbackMonth
    }
    return new Date(getValidYear(), getValidMonth() - 1, 15)
  }, [getDateFormat, value, defaultMonth])

  const [visibleMonth, setVisibleMonth] = useState(computeMonth)
  const resetVisibleMonth = useCallback(() => setVisibleMonth(computeMonth), [computeMonth])

  useEffect(() => {
    setVisibleMonth(computeMonth())
  }, [value, computeMonth])

  return { visibleMonth, setVisibleMonth, resetVisibleMonth }
}

function usePickerState() {
  const [isPickerOpen, setPickerOpen] = useState(false)
  const wasPickerOpenRecently = useDebounce(isPickerOpen, 200)
  return { isPickerOpen, setPickerOpen, wasPickerOpenRecently }
}

function useEscape(callback: () => void, { enable }: { enable?: boolean }) {
  useEffect(() => {
    if (enable) {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          callback()
          event.preventDefault()
        }
      }
      document.addEventListener('keydown', onKeyDown)
      return () => document.removeEventListener('keydown', onKeyDown)
    }
    return
  }, [enable, callback])
}

const defaultDatePickerCopy = {
  locale: enUS,
  dropdowns: {
    monthLabel: 'Month',
    yearLabel: 'Year',
  },
  navigation: {
    nextLabel: 'Go to next month',
    previousLabel: 'Go to previous month',
  },
}

export const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(
  ({ copy = defaultDatePickerCopy, picker, getDateFormat = getCustomDateFormat, ...props }, ref) => {
    const [uncontrolledValue, setUncontrolledValue] = useState(props.defaultValue ?? '')
    const value = String(props.value ?? uncontrolledValue)

    const { visibleMonth, setVisibleMonth, resetVisibleMonth } = useVisibleMonth(
      value,
      getDateFormat,
      picker?.defaultMonth
    )
    const { isPickerOpen, setPickerOpen, wasPickerOpenRecently } = usePickerState()

    const selected = useMemo(() => getDateFromFormattedDate(value, getDateFormat), [value, getDateFormat])

    useEscape(
      useCallback(() => setPickerOpen(false), [setPickerOpen]),
      { enable: isPickerOpen }
    )

    return (
      <DatePickerInputClickOutside onClickOutside={() => setPickerOpen(false)} enable={isPickerOpen}>
        <DatePicker
          {...picker}
          open={isPickerOpen}
          selected={selected}
          month={visibleMonth}
          closeOnSelect
          onClose={() => {
            setPickerOpen(false)
          }}
          onMonthChange={date => {
            picker?.onMonthChange?.(date)
            setVisibleMonth(date)
          }}
          onSelect={(date, ...rest) => {
            if (date) {
              const value = getFormattedDateFromDate(date, getDateFormat)
              props.onChange?.({ target: { value } } as ChangeEvent<HTMLInputElement>)
              setUncontrolledValue(value)
            }
            picker?.onSelect?.(date, ...rest)
          }}
          copy={copy}
        >
          {targetProps => (
            <Input
              {...targetProps}
              {...props}
              ref={ref}
              autoComplete="off"
              isVisuallyFocused={isPickerOpen || props.isVisuallyFocused}
              onChange={event => {
                props.onChange?.(event)
                setUncontrolledValue(event.target.value)
              }}
              onClick={event => {
                props.onClick?.(event)
                setPickerOpen(true)
                resetVisibleMonth()
              }}
              onFocus={event => {
                props.onFocus?.(event)
                // When the DatePicker returns focus to the initiating element (this input) by calling `.focus()`,
                // a focus event is still triggered. This check avoids reopening the input picker in this case.
                if (!isPickerOpen && !wasPickerOpenRecently) {
                  setPickerOpen(true)
                  resetVisibleMonth()
                } else {
                  event.target.selectionStart = event.target.selectionEnd = event.target.value.length
                }
              }}
              onInput={event => {
                const dateFormat = getDateFormat()
                const parseDate = dateFormat?.parseDate
                const isValid = !!parseDate && isValidFullYear(parseDate(event.currentTarget.value)?.year)
                setPickerOpen(!isValid)
              }}
            />
          )}
        </DatePicker>
      </DatePickerInputClickOutside>
    )
  }
)

export default DatePickerInput
